import { useEffect, useRef, useState } from "react";
import { styles } from "../styles";

/**
 * Collapsible header navigation. Replaces the old row of nav buttons with a
 * single "Menu ▾" that opens a dropdown — keeps the header clean on small
 * screens and gives room for the bell icon next to it.
 *
 * Items are passed in as an array of { label, onClick, hidden?, danger? }
 * so the parent owns which entries appear (admin-only, in-org-only, etc.)
 * and the dropdown stays a pure presentational component.
 */
export default function HeaderMenu({ items = [] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close when the user clicks anywhere outside the dropdown.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const visible = items.filter((it) => !it.hidden);
  if (visible.length === 0) return null;

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        style={{
          ...styles.button,
          padding: "6px 10px",
          lineHeight: 0,
        }}
        onClick={() => setOpen((o) => !o)}
        aria-label="Menu"
        title="Menu"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <rect x="3" y="5" width="18" height="2.2" rx="1" />
          <rect x="3" y="10.9" width="18" height="2.2" rx="1" />
          <rect x="3" y="16.8" width="18" height="2.2" rx="1" />
        </svg>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: "4px",
            minWidth: "180px",
            background: "white",
            border: "1px solid #ccc",
            borderRadius: "6px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            zIndex: 1000,
            padding: "4px 0",
          }}
        >
          {visible.map((it, i) => (
            <div
              key={i}
              onClick={async () => {
                try {
                  await it.onClick?.();
                } catch (err) {
                  console.error("HeaderMenu action failed:", it.label, err);
                } finally {
                  setOpen(false);
                }
              }}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: "13px",
                color: it.danger ? "#c12c2c" : "#222",
                borderTop: i > 0 ? "1px solid #f0f0f0" : "none",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f4f8ff")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "white")
              }
            >
              {it.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
